import React, { useState } from 'react';

import PopsData from '~/data/sidebar/pops.json';
import worldMapLight from '~/images/pops-map.svg';
import { getVisibility } from '../../../utils/helpers';

const NETWORK_TYPE = {
  Standard: 'standard_network',
  Volume: 'volume_network',
  ISP: 'isp_network',
};

const items = [
  {
    name: 'standard_network',
    label: 'Standard Network',
    popLabel: '',
  },
  {
    name: 'volume_network',
    label: 'Volume Network',
    popLabel: 'Volume',
  },
  {
    name: 'isp_network',
    label: 'ISP Network',
    popLabel: 'ISP',
  },
];

interface ToggleFilterProps {
  items: {
    name: string;
    label: string;
    popLabel?: string;
  }[];
  updateFilter: (idx: number) => void;
  color?: string;
}

export const ToggleFilter: React.FC<ToggleFilterProps> = ({
  items,
  updateFilter,
  color = 'default',
}) => {
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState('');

  const colorClass = color ? `toggle-filter--${color}` : '';

  const clickHandler = (clickedIndex: number) => () => {
    // const [filter, setFilter] = useState(NETWORK_TYPE.Standard)
    if (clickedIndex !== active) {
      setActive(clickedIndex);
      const direction = clickedIndex < active ? 'left' : 'right';
      const extra =
        items.length > 2 && clickedIndex + active === items.length - 1
          ? ' extra'
          : '';
      setDirection(`${direction}${extra}`);
      updateFilter(clickedIndex);
    }
  };

  if (!items) {
    return null;
  }

  return (
    <div className={`toggle-filter ${colorClass}`}>
      {items.map((item, index) => {
        const activeClass = index === active ? 'active' : '';
        const directionClass = index === active ? `${direction}` : '';

        return (
          <div
            key={item.name || item.label}
            className={`toggle-filter__item ${activeClass} ${directionClass}`}
            onClick={clickHandler(index)}
          >
            {item.label}
          </div>
        );
      })}
    </div>
  );
};

interface PopsMapProps {
  block: {
    block_visibility: string;
    _template: string;
    css_class_name: string;
    hide_filter: boolean;
    legend_visibility: string;
  };
}

const PopsMap: React.FC<PopsMapProps> = ({ block }) => {
  const [filter, setFilter] = useState(NETWORK_TYPE.Standard);
  const blockVisibility = getVisibility(block.block_visibility);

  const changeFilter = (idx: number) => {
    if (items?.length > 0 && idx >= 0 && idx < items.length && items[idx]) {
      setFilter(items[idx].name);
    } else {
    }
  };

  return (
    <section
      className={`block ${blockVisibility} ${block._template} ${block.css_class_name}`}
    >
      <div className='container'>
        <div className='pops-map-wr'>
          <img
            src={worldMapLight}
            alt='CDN World Map - bunny.net'
            title='CDN World Map - bunny.net'
          />
          <ul className='pops-dots'>
            {PopsData.regions.map((region, index) => {
              if (!region.pops || region.pops.length <= 0) {
                return null;
              }

              var pops = region.pops.map((pop, index) => {
                const popType = pop.type.split('_')[0];
                const popLabel = items.find(
                  item => item.name === pop.type
                )?.popLabel;
                const hasDonutShape = pop.type === NETWORK_TYPE.ISP;
                let dotStyle: React.CSSProperties = {
                  background: `${region.map_color}`,
                };
                const signalStyle: React.CSSProperties = {
                  background: `radial-gradient(circle,rgba(255,143,79,.14889705882352944) 0%,${region.map_color} 100%)`,
                };

                if (hasDonutShape) {
                  dotStyle = {
                    background: 'none',
                    borderColor: `${region.map_color}`,
                  };
                }

                let popNameOnMap = pop.name;
                if (popNameOnMap.includes(',')) {
                  popNameOnMap = popNameOnMap.substring(
                    0,
                    popNameOnMap.indexOf(',')
                  );
                }

                if (
                  (filter === NETWORK_TYPE.Standard || pop.type === filter) &&
                  pop.show_on_map
                ) {
                  return (
                    <li
                      key={index}
                      className={`pop ${popType}`}
                      style={{
                        top: `${pop.map_position_top}`,
                        left: `${pop.map_position_left}`,
                      }}
                    >
                      <div
                        className='pop-dot'
                        style={dotStyle}
                      />
                      <div
                        className='pop-signal'
                        style={signalStyle}
                      />
                      <div
                        className={`label ${region.dark_font_color ? 'dark' : ''}`}
                        style={{ background: `${region.map_color}` }}
                      >
                        <span
                          style={{ borderTopColor: region.map_color }}
                          className={`label-arrow`}
                        ></span>
                        {popNameOnMap} {popLabel && <small>({popLabel})</small>}
                      </div>
                    </li>
                  );
                }
                return '';
              });
              return <React.Fragment key={index}>{pops}</React.Fragment>;
            })}
          </ul>
        </div>

        {!block.hide_filter && (
          <ToggleFilter
            items={items}
            updateFilter={changeFilter}
          />
        )}

        <PopsLegend
          regions={PopsData.regions}
          visibility={block.legend_visibility}
        />
      </div>
    </section>
  );
};

type Region = {
  name: string;
  type?: string;
  color?: string;
  show_on_map?: boolean;
  show_on_list?: boolean;
  pops?: Pop[];
  map_color: string;
  list_color: string;
  show_on_legend: boolean;
  region_class: string;
  dark_font_color: boolean;
  name_mobile: string;
};

type Pop = {
  name: string;
  type: string;
  show_on_map?: boolean;
  show_on_list?: boolean;
  map_position_top: string;
  map_position_left: string;
  planned?: boolean;
  region_map_position_top?: string;
  region_map_position_left?: string;
  flag?: string;
};

interface PopsLegendProps {
  regions: Region[];
  visibility: string;
}
const PopsLegend: React.FC<PopsLegendProps> = ({ regions, visibility }) => {
  const legendVisibility = getVisibility(visibility);

  if (!legendVisibility) {
    return null;
  }

  return (
    <div className='pops-legend-wrapper'>
      <ul className={`${legendVisibility} pops-legend`}>
        {regions
          .map((region, index) => {
            if (region.show_on_legend) {
              let popsCount = !region.pops ? 0 : region.pops.length;

              return (
                <li key={index}>
                  <span
                    style={{ borderColor: `${region.map_color}` }}
                    className='circle'
                  ></span>
                  {region.name_mobile ? (
                    <>
                      <span className={`is-desktop`}>
                        {region.name} ({popsCount})
                      </span>
                      <span className={`is-mobile`}>
                        {region.name_mobile} ({popsCount})
                      </span>
                    </>
                  ) : (
                    <span>
                      {region.name} ({popsCount})
                    </span>
                  )}
                </li>
              );
            }
            return null;
          })
          .filter(Boolean)}
      </ul>
    </div>
  );
};

export default PopsMap;
